import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { GET_POLICY_VEHICLE_EXEMPTIONS, PolicyVehicleExemptionSummary } from "common/graphql";
import { useMutation } from "@apollo/client";
import { END_VEHICLE_EXEMPTION, EndVehicleExemptionData, EndVehicleExemptionVars } from "common/graphql/mutations";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";

interface RemoveVehicleExemptionDialogProps {
  vehicleExemption?: PolicyVehicleExemptionSummary;
  open: boolean;
  onClose: () => void;
}

export const RemoveVehicleExemptionDialog: React.FC<RemoveVehicleExemptionDialogProps> = ({
  open,
  onClose,
  vehicleExemption,
}) => {
  const [endVehicleExemption, { loading: isSubmitting }] = useMutation<
    EndVehicleExemptionData,
    EndVehicleExemptionVars
  >(END_VEHICLE_EXEMPTION);

  const { publishSnackbarMessage } = useSnackPackContext();

  const handleSubmit = () => {
    if (!vehicleExemption) {
      return;
    }

    endVehicleExemption({
      variables: {
        policyVehicleExemptionId: vehicleExemption.id,
        endAt: new Date(),
      },
      onCompleted() {
        onClose();
      },
      onError(err) {
        publishSnackbarMessage({
          message: "Failed to remove vehicle exemption. Please try again",
          severity: "error",
        });
        console.error(err);
      },
      refetchQueries: [GET_POLICY_VEHICLE_EXEMPTIONS],
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove Vehicle Exemption</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Are you sure you want to remove the exemption for{" "}
          {vehicleExemption?.registeredVehicle?.stateCode ?? vehicleExemption?.licensePlateStateCodeLike}
          {vehicleExemption?.registeredVehicle?.licensePlate ?? vehicleExemption?.licensePlateNumberLike}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button fullWidth onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          fullWidth
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          Remove Exemption
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveVehicleExemptionDialog;
